import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import { getTextBlockSize } from '@Utils/3d';
import { Flex } from '@react-three/flex';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { ThreeUpMoreCta } from './ThreeUpMoreCta';
import { ThreeUpParagraph } from './ThreeUpParagraph';

type Props = {
  gridCard: GridCard<PlaneTypes.Layout_ThreeUp>;
};

export const ThreeUpTextPart = observer(function ThreeUpTextPart({
  gridCard,
}: Props) {
  const data = gridCard.data!;
  const { textPart } = data.styles!;

  const { paragraphs } = data.styles!.textPart;

  const [paragraphHeights, setParagraphHeights] = useState<
    number[] | undefined
  >();

  useEffect(() => {
    calculateParagraphHeights().then();
  }, [...paragraphs]);

  const calculateParagraphHeights = async (): Promise<void> => {
    const sizes = await Promise.all(
      paragraphs.map((paragraph) => getTextBlockSize(paragraph))
    );

    const heights = sizes.map((size) => size.height);
    setParagraphHeights(heights);
  };

  // Because 'Flex' does not 'repaint' itself even when heights have
  // changed we do not render it until we have the final results of
  // the heights we want to paint
  if (!paragraphHeights) {
    return null;
  }

  return (
    <group position={[textPart.position.x, textPart.position.y, 0]}>
      <Flex
        position={[0, 0, 0]}
        flexDirection='column'
        size={[
          textPart.textContentsContainer.width,
          textPart.textContentsContainer.height,
          0,
        ]}
        justifyContent='center'
        alignItems='flex-start'
        centerAnchor
      >
        {textPart.paragraphs.map((paragraph, index) => (
          <ThreeUpParagraph
            key={index + paragraph.text}
            textProps={paragraph}
            index={index}
            height={paragraphHeights[index]}
            marginTop={22}
          />
        ))}
        {Boolean(data.dtoData.linkButtonUrl) &&
          Boolean(data.dtoData.linkButtonTitle) && (
            <ThreeUpMoreCta gridCard={gridCard} marginTop={40} />
          )}
      </Flex>
    </group>
  );
});
